/**
 * Colour scales of the legacy Vuexy theme (src/@core/scss/base/bootstrap-extended/_variables.scss at legacy/vue2).
 * Each 50–950 scale is the legacy colour at 500, tinted towards white (50–400: 95, 76, 57, 38, 19 %) and shaded
 * towards black (600–950: 15, 30, 45, 60, 75 %) — the rule PrimeVue's own `palette()` uses — written out so a
 * reviewer can compare values without running anything. The mapping is documented in ./README.md.
 */

export type Scale = Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950, string>

/** `$primary: #5a5aff` */
export const primary: Scale = {
  50: '#f7f7ff',
  100: '#d7d7ff',
  200: '#b8b8ff',
  300: '#9999ff',
  400: '#7979ff',
  500: '#5a5aff',
  600: '#4c4cd9',
  700: '#3f3fb2',
  800: '#32328c',
  900: '#242466',
  950: '#161640',
}

/** `$success: $green: #28c76f` */
export const success: Scale = {
  50: '#f4fcf8',
  100: '#cbf2dc',
  200: '#a3e7c1',
  300: '#7adca6',
  400: '#51d28a',
  500: '#28c76f',
  600: '#22a95e',
  700: '#1c8b4e',
  800: '#166d3d',
  900: '#10502c',
  950: '#0a321c',
}

/** `$info: $blue: #00cfe8` */
export const info: Scale = {
  50: '#f2fdfe',
  100: '#c2f3f9',
  200: '#91eaf5',
  300: '#61e1f1',
  400: '#30d8ec',
  500: '#00cfe8',
  600: '#00b0c5',
  700: '#0091a2',
  800: '#007280',
  900: '#00535d',
  950: '#00343a',
}

/** `$warning: $orange: #ff9f43` */
export const warning: Scale = {
  50: '#fffaf6',
  100: '#ffe8d2',
  200: '#ffd6ae',
  300: '#ffc38a',
  400: '#ffb167',
  500: '#ff9f43',
  600: '#d98739',
  700: '#b26f2f',
  800: '#8c5725',
  900: '#66401b',
  950: '#402811',
}

/** `$danger: $red: #ea5455` */
export const danger: Scale = {
  50: '#fef6f6',
  100: '#fad6d6',
  200: '#f6b5b6',
  300: '#f29596',
  400: '#ee7475',
  500: '#ea5455',
  600: '#c74748',
  700: '#a43b3b',
  800: '#812e2f',
  900: '#5e2222',
  950: '#3a1515',
}

/**
 * Surface greys placed so Aura's references land on the legacy values: Aura reads body text from 700, hover text
 * from 800, muted text and placeholders from 500, input borders from 300, borders from 200, hover backgrounds from 100.
 */
export const surface: Scale & { 0: string } = {
  0: '#ffffff', // cards, inputs ($white)
  50: '#f8f8f8', // page background ($body-bg)
  100: '#f3f2f7', // table head, pagination, hover ($table-head-bg, $pagination-bg)
  200: '#ebe9f1', // borders ($border-color)
  300: '#d8d6de', // input and checkbox borders ($custom-control-border-color)
  400: '#babfc7', // icons ($gray-100)
  500: '#b9b9c3', // muted text, placeholders ($text-muted)
  600: '#82868b', // secondary ($secondary)
  700: '#6e6b7b', // body text ($body-color)
  800: '#5e5873', // headings ($headings-color)
  900: '#4b4b4b', // dark ($dark)
  950: '#22292f', // black ($black)
}

/**
 * The dark scheme's greys, in the same roles as the light scale (0 = cards, 50 = page, 200 = borders, 700 = body text,
 * 950 = the strongest text), so the shell's token use reads the same in both schemes: values from the dark proposal on
 * the sidebar design canvas (page #12151f, card #1e2235, border #2a2f45, text #a6abc0, headings #f1f2f8).
 */
export const surfaceDark: Scale & { 0: string } = {
  0: '#1e2235', // cards, inputs
  50: '#12151f', // page background
  100: '#272c45', // table head, hover
  200: '#2a2f45', // borders
  300: '#394060', // input borders
  400: '#5d6279', // icons
  500: '#6b7088', // muted text, placeholders
  600: '#8b90a5', // secondary
  700: '#a6abc0', // body text
  800: '#d9dbe6', // headings
  900: '#f1f2f8', // strong text
  950: '#ffffff', // strongest text
}

/** The software switcher's button colours (positional in legacy, wp/shell §2): ac, hr, re, on, en; then pm (new). */
export const softwareColors = {
  ac: '#b748ff',
  hr: '#28c77a',
  re: '#5a5aff',
  on: '#26a7ff',
  en: '#ff9f43',
  pm: '#ea5455',
} as const
